import React, { memo, useCallback } from 'react';
import { useRouter } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { IconLink, theme, TransparentModal, useResponsive } from '@sg/garnish';

import { useTrackEventEffect } from '@order/Telemetry';

import { LocationSearch, PageWrapper } from '../../components';
import { useReorderState } from '../ReorderingScreen';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationsScreen = memo(() => {
  const { currentBreakpoint } = useResponsive();
  const {
    reordering: isReordering,
    restaurantSlug: reorderRestaurantSlug,

    deliveryOrderDetail,
  } = useReorderState();

  const interactedLocationRestaurantSlug = isReordering ? reorderRestaurantSlug : undefined;
  const interactedLocationDeliveryAddressId = isReordering
    ? deliveryOrderDetail?.addressId
    : undefined;

  // ─── Effects ─────────────────────────────────────────────────────────

  useTrackEventEffect({ name: 'location.view' });

  // ─────────────────────────────────────────────────────────────────────

  if (currentBreakpoint.isXS) {
    return (
      <View style={styles.wrapper}>
        <LocationSearch
          interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
          interactedLocationDeliveryAddressId={interactedLocationDeliveryAddressId}
        />
      </View>
    );
  }

  return (
    <PageWrapper contentContainerStyle={styles.wrapper}>
      <LocationSearch
        interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
        interactedLocationDeliveryAddressId={interactedLocationDeliveryAddressId}
      />
    </PageWrapper>
  );
});

// ─────────────────────────────────────────────────────────────────────────────

export const LocationSelectionModalScreen = () => {
  const router = useRouter();
  const { currentBreakpoint } = useResponsive();
  const { formatMessage } = useIntl();
  const {
    reordering: isReordering,
    restaurantSlug: reorderRestaurantSlug,
    deliveryOrderDetail,
  } = useReorderState();

  // ─── Flags ─────────────────────────────────────────────────────────────────

  const shouldShowModalCloseIcon = !currentBreakpoint.isXS;

  // ─── Derived ───────────────────────────────────────────────────────────────

  const interactedLocationRestaurantSlug = isReordering ? reorderRestaurantSlug : undefined;
  const interactedLocationDeliveryAddressId = isReordering
    ? deliveryOrderDetail?.addressId
    : undefined;

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const dismissModal = useCallback(() => {
    if (router.canGoBack()) {
      router.back();

      return;
    }

    router.dismissTo('/');
  }, [router]);

  // ─── Styles ────────────────────────────────────────────────────────────────

  const modalOuterContainerStyles = [
    styles.modalOuterContainer,
    shouldShowModalCloseIcon ? styles.modalOuterContainerSM : undefined,
  ];
  const modalInnerContainerStyles = [
    styles.modalInnerContainer,
    shouldShowModalCloseIcon ? styles.modalInnerContainerSM : undefined,
  ];

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <TransparentModal
      accessibilityLabel={formatMessage(messages.dialogA11yLabel)}
      size="large"
      dialogMaxWidth={DIALOG_MAX_WIDTH}
      dialogMaxHeight={DIALOG_MAX_HEIGHT}
      dialogStyles={styles.modalDialog}
      onClose={dismissModal}
      transparent
      withBackdrop
    >
      <View style={modalOuterContainerStyles}>
        {shouldShowModalCloseIcon ? (
          <IconLink
            name="IconClose"
            palette="white"
            width={40}
            height={40}
            accessibilityLabel={formatMessage(messages.closeA11yLabel)}
            onPress={dismissModal}
            style={styles.modalCloseIcon}
          />
        ) : undefined}

        <View style={modalInnerContainerStyles}>
          <LocationSearch
            interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
            interactedLocationDeliveryAddressId={interactedLocationDeliveryAddressId}
            isInsideModal={true}
            onNavClose={dismissModal}
          />
        </View>
      </View>
    </TransparentModal>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  dialogA11yLabel: {
    defaultMessage: 'Select a location',
    description: 'Locations | Location search | Dialog a11y label',
  },
  closeA11yLabel: {
    defaultMessage: 'Close',
    description: 'Locations | Location search | Close button a11y label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  modalDialog: {
    boxShadow: 'none', // override default styles
    elevation: 0,
  },
  wrapper: {
    height: '100%',
  },
  modalOuterContainer: {
    flex: 1,
    gap: theme.spacing['4'],
  },
  modalOuterContainerSM: {
    padding: theme.spacing['6'],
  },
  modalInnerContainer: {
    flex: 1,
    overflow: 'hidden',
  },
  modalInnerContainerSM: {
    borderRadius: theme.radius.xlarge,
    boxShadow: `3px 3px 32px -10px ${theme.colors.OPACITY.DARK_KALE.DARK}`,
  },
  modalCloseIcon: {
    marginLeft: 'auto',
    boxShadow: `3px 3px 32px -10px ${theme.colors.OPACITY.DARK_KALE.DARK}`,
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const DIALOG_MAX_WIDTH = 1280;
const DIALOG_MAX_HEIGHT = 870;
